import React, { useContext, useState } from "react";
import { useMutation } from "@apollo/client";
import { Alert } from "@mui/material";
import { useTranslation } from "react-i18next";

import { ApplicantModel } from "models";
import { FormActions, Notification } from "components";
import { DataFormsContext } from "context/DataFormsContext";
import {
  CREATE_ENROLLMENT,
  CREATE_ENROLLMENT_data,
  CREATE_ENROLLMENT_vars,
} from "graphql/mutation/createEnrollment";
import { reportCreateEnrollmentFailure } from "monitoring/createEnrollmentTelemetry";

import { ApplicantForm } from "./ApplicantForm";
import { ApplicationSubmitted } from "./ApplicationSubmitted";
import { useGetDefaultFormValues } from "./useGetDefaultFormValues";
import { prepareDataForRequest } from "./utils";
import { Container, HomePageContainer } from "./Forms.css";

// Referral-only flow: first page + submit. Full multi-step form is preserved below (commented out).
export const Forms = () => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { data, resetData } = useContext(DataFormsContext);
  const { defaultApplicantMember } = useGetDefaultFormValues(data);

  const [createEnrollment, { loading: loadingCreateEnrollment }] = useMutation<
    CREATE_ENROLLMENT_data,
    CREATE_ENROLLMENT_vars
  >(CREATE_ENROLLMENT);

  const onCreateEnrollment = async (values: Partial<ApplicantModel>) => {
    let variables: CREATE_ENROLLMENT_vars;
    try {
      variables = prepareDataForRequest(values);
    } catch (err) {
      reportCreateEnrollmentFailure({ phase: "prepare", error: err });
      setErrorMessage(
        err instanceof Error ? err.message : "Could not prepare enrollment data",
      );
      return;
    }

    try {
      const res = await createEnrollment({ variables });
      if (res.errors?.length) {
        const graphqlMessages = res.errors.map((e) => e.message);
        setErrorMessage(graphqlMessages.join("; "));
        reportCreateEnrollmentFailure({
          phase: "graphql",
          graphqlMessages,
          variables,
        });
        return;
      }
      const payload = res.data?.createEnrollment;
      if (payload?.success && payload.caseAccessId) {
        resetData();
        setErrorMessage(null);
        setIsSubmitted(true);
        return;
      }
      setErrorMessage(
        payload?.message ??
          "Enrollment was not completed. Please try again or contact support.",
      );
      reportCreateEnrollmentFailure({
        phase: "mutation_response",
        mutationMessage: payload?.message ?? null,
        variables,
      });
    } catch (err) {
      console.error("createEnrollment failed", err);
      const message =
        err instanceof Error ? err.message : "Network error while submitting";
      setErrorMessage(message);
      reportCreateEnrollmentFailure({
        phase: "network",
        error: err,
        variables,
      });
    }
  };

  if (isSubmitted) {
    return <ApplicationSubmitted onStartNew={() => setIsSubmitted(false)} />;
  }

  return (
    <Container>
      <Notification
        type="error"
        setMessage={setErrorMessage}
        message={errorMessage}
      />
      <HomePageContainer>
        <Alert severity="info">{t("welcome_notification_message")}</Alert>
        <FormActions
          title="household_info"
          form={
            <ApplicantForm
              defaultValues={defaultApplicantMember}
              disableBackButton
              disableNextButton={loadingCreateEnrollment}
              onSubmit={onCreateEnrollment}
              onBack={() => undefined}
            />
          }
        />
      </HomePageContainer>
    </Container>
  );
};

/*
=== PREVIOUS FULL MULTI-STEP FORM (kept for restore) ===

import React, { useContext, useState } from "react";
import i18n from "i18n";
import { useMutation } from "@apollo/client";
import { Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { IncomeTypeModel, SecurityQuestionsModel } from "models";
import { FormActions, Notification } from "components";
import { usePersistentCurrentStep } from "hooks";
import { FollowingQuestionsForm } from "components/pages/DhsPage/Forms/FollowingQuestionsForm";
import { ImmigrationForm } from "components/pages/DhsPage/Forms/ImmigrationForm";
import { RenewingCoverageFutureForm } from "components/pages/DhsPage/Forms/RenewingCoverageFutureForm";
import { SecurityQuestionsForm } from "components/pages/DhsPage/Forms/SecurityQuestionsForm";
import { DataFormsContext } from "context/DataFormsContext";
import { CREATE_ENROLLMENT, CREATE_ENROLLMENT_data, CREATE_ENROLLMENT_vars, } from "graphql/mutation/createEnrollment";
import { getTaxableYear } from "utils/dates";
import { routes } from "routes";

import { PregnancyForm } from "./PregnancyForm";
import { TaxFillingForm } from "./TaxFillingForm";
import { DetailsJobForm } from "./DetailsJobForm/DetailsJobMembersForm";
import { ApplicantForm } from "./ApplicantForm";
import { HouseholdMemberListForm } from "./HouseholdMemberListForm";
import { useGetDefaultFormValues } from "./useGetDefaultFormValues";
import { IncomeQuestionForm } from "./IncomeQuestionForm";
import { IncomeTypeForm } from "./IncomeTypeForm";
import { HealthCoverageForm } from "./HealthCoverageForm";
import { PAIForm } from "./PAIForm";
import { hasAnyoneDetailedTypeJob, prepareDataForRequest } from "./utils";
import { reportCreateEnrollmentFailure } from "monitoring/createEnrollmentTelemetry";
import { Container, HomePageContainer } from "./Forms.css";
import { GeneralForm } from "./types";

interface HouseHoldForms {
  [key: number]: JSX.Element;
}

export const Forms = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {data, updateData, resetData} = useContext(DataFormsContext);
  const [currentStep, setCurrentStep] = usePersistentCurrentStep(1);
  const {
    defaultSecurityQuestionsFormValues,
    defaultApplicantMember,
    defaultHouseholdMembers,
    defaultPregnancyMembers,
    defaultTaxFillingMembers,
    defaultIncomeQuestionMembers,
    defaultIncomeTypeMembers,
    defaultDetailJobMembers,
    defaultPaiMembers,
    defaultFollowingQuestionsMembers,
    defaultHealthCoverageMembers,
    defaultRenewingCoverage,
    defaultImmigrationMembers,
  } = useGetDefaultFormValues(data);

  const [createEnrollment, { loading: loadingCreateEnrollment }] = useMutation<
    CREATE_ENROLLMENT_data,
    CREATE_ENROLLMENT_vars
  >(CREATE_ENROLLMENT);

  const taxableYear = getTaxableYear()

  const disableBackButton = currentStep === 1;

  const onSubmit = <T, >(values: T, nextStep = 1, redirectOnly = false) => {
    if (!redirectOnly) {
      updateData((prevData: any) => ({
        ...prevData,
        [currentStep]: values,
      }));
    }
    setCurrentStep((prev) => prev + nextStep);
  };

  const onCreateEnrollment = async (
    values: Partial<SecurityQuestionsModel>,
  ) => {
    updateData((prevData: any) => {
      return {
        ...prevData,
        [currentStep]: values,
      };
    });

    let variables: CREATE_ENROLLMENT_vars;
    try {
      // NOTE: previous signature was prepareDataForRequest(data, values)
      variables = prepareDataForRequest(data, values);
    } catch (err) {
      reportCreateEnrollmentFailure({ phase: "prepare", error: err });
      setErrorMessage(
        err instanceof Error ? err.message : "Could not prepare enrollment data",
      );
      return;
    }

    try {
      const res = await createEnrollment({ variables });
      if (res.errors?.length) {
        const graphqlMessages = res.errors.map((e) => e.message);
        setErrorMessage(graphqlMessages.join("; "));
        reportCreateEnrollmentFailure({
          phase: "graphql",
          graphqlMessages,
          variables,
        });
        return;
      }
      const payload = res.data?.createEnrollment;
      if (payload?.success && payload.caseAccessId) {
        resetData();
        setErrorMessage(null);
        navigate(`${routes.upload.replace(":id", payload.caseAccessId)}`);
        return;
      }
      setErrorMessage(
        payload?.message ??
          "Enrollment was not completed. Please try again or contact support.",
      );
      reportCreateEnrollmentFailure({
        phase: "mutation_response",
        mutationMessage: payload?.message ?? null,
        variables,
      });
    } catch (err) {
      console.error("createEnrollment failed", err);
      const message =
        err instanceof Error ? err.message : "Network error while submitting";
      setErrorMessage(message);
      reportCreateEnrollmentFailure({
        phase: "network",
        error: err,
        variables,
      });
    }
  };

  const onBack = (stepBack = 1, redirectOnly = false) => {
    setCurrentStep((prev: number) => prev - Number(stepBack));
    if (!redirectOnly) {
      updateData((prevData: GeneralForm | null) => {
        const currentStepString =
          currentStep.toString() as unknown as keyof GeneralForm;
        if (prevData && prevData[currentStepString]) {
          const newData = {...prevData};
          delete newData[currentStepString];
          return newData;
        }
        return prevData;
      });
    }
  };

  const onBackCoverageHealthForm = () => {
    const hasAnyoneIncomeQuestion = data?.[5]?.incomeMembers?.some(
      (m) => m.hasIncome,
    );
    const incomeTypeData = data?.[6]?.incomeTypeMembers;
    const isAnyoneHasIncome = hasAnyoneDetailedTypeJob(incomeTypeData ?? []);
    const hasIncomeQuestionOnly = hasAnyoneIncomeQuestion && !isAnyoneHasIncome;
    const hasIncomeOnly = !hasAnyoneIncomeQuestion && isAnyoneHasIncome;
    let stepToGoBack = 1;

    if (!isAnyoneHasIncome && !hasAnyoneIncomeQuestion) {
      stepToGoBack = 4;
    }
    if (hasIncomeQuestionOnly || hasIncomeOnly) {
      stepToGoBack = 3;
    }
    onBack(stepToGoBack);
  };

  const forms: HouseHoldForms = {
    1: (
      <HomePageContainer>
        <Alert severity="info">{t("welcome_notification_message")}</Alert>
        <FormActions
          title="household_info"
          form={
            <ApplicantForm
              defaultValues={defaultApplicantMember}
              disableBackButton={disableBackButton}
              onSubmit={onSubmit}
              onBack={onBack}
            />
          }
        />
      </HomePageContainer>
    ),
    2: (
      <FormActions
        title="household_member_list"
        form={
          <HouseholdMemberListForm
            defaultValues={defaultHouseholdMembers}
            disableBackButton={disableBackButton}
            onSubmit={onSubmit}
            onBack={onBack}
          />
        }
      />
    ),
    3: (
      <FormActions
        title="pregnancy_form_title"
        form={
          <PregnancyForm
            defaultValues={defaultPregnancyMembers}
            onBack={onBack}
            onSubmit={onSubmit}
          />
        }
      />
    ),
    4: (
      <FormActions
        title="tax_filling_form_title"
        subtitle={i18n.t("tax_filling_form_subtitle", {
          year: taxableYear,
        })}
        form={
          <TaxFillingForm
            onSubmit={onSubmit}
            onBack={onBack}
            defaultValues={defaultTaxFillingMembers}
          />
        }
      />
    ),
    5: (
      <FormActions
        title="income_form_title"
        subtitle="income_form_subtitle"
        form={
          <IncomeQuestionForm
            defaultValues={defaultIncomeQuestionMembers}
            onSubmit={onSubmit}
            onBack={onBack}
          />
        }
      />
    ),
    6: (
      <FormActions
        title="income_type_form_title"
        form={
          <IncomeTypeForm
            defaultValues={defaultIncomeTypeMembers}
            onSubmit={onSubmit}
            onBack={onBack}
          />
        }
      />
    ),
    7: (
      <DetailsJobForm
        incomeTypeMembers={data?.[6]?.incomeTypeMembers as IncomeTypeModel[]}
        defaultValues={defaultDetailJobMembers}
        onSubmit={onSubmit}
        onBack={onBack}
      />
    ),
    8: (
      <FormActions
        title={i18n.t("pai_form_title", {
          year: taxableYear
        })}
        subtitle={i18n.t("pai_form_subtitle", {
          year: taxableYear,
        })}
        form={
          <PAIForm
            defaultValues={defaultPaiMembers}
            onSubmit={onSubmit}
            onBack={onBack}
          />
        }
      />
    ),
    9: (
      <FormActions
        title="health_coverage_form_title"
        form={
          <HealthCoverageForm
            defaultValues={defaultHealthCoverageMembers}
            onSubmit={onSubmit}
            onBack={onBackCoverageHealthForm}
          />
        }
      />
    ),
    10: (
      <FormActions
        title="immigration_status"
        form={
          <ImmigrationForm
            defaultValues={defaultImmigrationMembers}
            onBack={onBack}
            onSubmit={onSubmit}
          />
        }
      />
    ),
    11: (
      <FormActions
        title="following_question_title_form"
        form={
          <FollowingQuestionsForm
            defaultValues={defaultFollowingQuestionsMembers}
            onSubmit={onSubmit}
            onBack={onBack}
          />
        }
      />
    ),
    12: (
      <FormActions
        title="renewing_coverage_form_title"
        subtitle="renewing_coverage_form_subtitle"
        form={
          <RenewingCoverageFutureForm
            onSubmit={onSubmit}
            onBack={onBack}
            defaultValues={defaultRenewingCoverage}
          />
        }
      />
    ),
    13: (
      <FormActions
        title="account_security_questions_title"
        form={
          <SecurityQuestionsForm
            disableNextButton={loadingCreateEnrollment}
            defaultValues={defaultSecurityQuestionsFormValues}
            disableBackButton={disableBackButton}
            onSubmit={onCreateEnrollment}
            onBack={onBack}
          />
        }
      />
    ),
  };

  return (
    <Container>
      <Notification
        type="error"
        setMessage={setErrorMessage}
        message={errorMessage}
      />
      {forms[currentStep]}
    </Container>
  );
};

=== END PREVIOUS FULL MULTI-STEP FORM ===
*/
